import React from "react";
import { useTranslation } from "react-i18next";
import {
  useProjectsTransfer,
  useRetrieveZonesIndicators,
} from "@planzone/view-models";
import { zone, id, filter } from "@planzone/dto";

import { Button } from "@common/button";
import { Flag, InfiniteScroll } from "@common/infiniteScroll";
import { Props } from "./types";
import { DestinationsList } from "./destinationsList";
import { ProjectsList } from "./projectsList";
import { Loader } from "./loader";
import styles from "./styles.scss";

function ProjectsTransfer(props: Props) {
  const { t } = useTranslation();
  const [destination, setDestination] = React.useState({ ...zone, uuid: id });
  const { isRequestPending: isTransferPending, transferProjects } =
    useProjectsTransfer(destination);
  const {
    isRequestPending,
    zones,
    index,
    canRetrieveNextZonesIndicators,
    retrieveZonesIndicators,
  } = useRetrieveZonesIndicators(filter, "ascendingName", 10);
  const [projects, setProjects] = React.useState(props.projects);
  const filteredZones = zones.filter(({ uuid }) => uuid !== props.zone.uuid);

  return (
    <div className={styles.projectsTransfer}>
      <div className={styles.main}>
        <div className={styles.projects}>
          <h1 className={styles.title}>
            {t("projectsTransfer.projects.title")}
          </h1>
          <div className={styles.zonesList}>
            <ProjectsList
              zone={props.zone}
              selectedProjects={projects}
              selectProjects={setProjects}
            />
          </div>
        </div>
        <div className={styles.destination}>
          <InfiniteScroll className={styles.wrapper} canFetchOnIdle>
            {() => (
              <>
                <div className={styles.destinationHeader}>
                  <h1 className={styles.title}>
                    {t("projectsTransfer.title")}
                  </h1>
                  <p className={styles.message}>
                    {t("projectsTransfer.message", { name: props.zone.name })}
                  </p>
                </div>
                <div className={styles.zonesList}>
                  <DestinationsList
                    zones={filteredZones}
                    selectedZone={destination}
                    selectZone={setDestination}
                  />
                  {isRequestPending && <Loader />}
                  <Flag
                    name="zones"
                    isDisabled={
                      !canRetrieveNextZonesIndicators || isRequestPending
                    }
                    retrieveItems={() =>
                      !isRequestPending &&
                      retrieveZonesIndicators({
                        filter,
                        sort: "ascendingName",
                        size: 10,
                        index,
                      })
                    }
                  />
                </div>
                <div className={styles.buttons}>
                  <Button type="cancel" press={props.cancel}>
                    {t("projectsTransfer.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    status={isTransferPending ? "pending" : "none"}
                    isDisabled={destination.uuid === id || isTransferPending}
                    press={() => transferProjects({ projects, destination })}
                  >
                    {t("projectsTransfer.confirm")}
                  </Button>
                </div>
              </>
            )}
          </InfiniteScroll>
        </div>
      </div>
    </div>
  );
}

export { ProjectsTransfer };
